import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { 
  Key, 
  Copy, 
  Download, 
  Upload, 
  Trash2, 
  Settings, 
  BarChart3, 
  Maximize, 
  Minimize, 
  Moon, 
  Sun,
  Check,
  RefreshCw,
  Lock,
  Zap,
  Grid,
  List,
  Plus,
  Minus,
  Eye,
  EyeOff,
  AlertCircle,
  Clock,
  Shield,
  Globe,
  Code
} from 'lucide-react';

interface TokenSettings {
  tokenType: 'access_token' | 'refresh_token' | 'authorization_code' | 'jwt' | 'client_credentials';
  grantType: 'authorization_code' | 'client_credentials' | 'resource_owner' | 'refresh_token';
  expiresIn: number;
  batchSize: number;
  includeScope: boolean;
  customScopes: string[];
  includeRefreshToken: boolean;
  jwtHeader: any;
  jwtPayload: any;
  clientIdLength: number;
  clientSecretLength: number;
  useSecureRandom: boolean;
  includeState: boolean;
  includePKCE: boolean;
}

interface GeneratedToken {
  id: string;
  tokenType: string;
  grantType: string;
  accessToken?: string;
  refreshToken?: string;
  authorizationCode?: string;
  clientId?: string;
  clientSecret?: string;
  tokenValue: string;
  expiresIn: number;
  expiresAt: Date;
  scope?: string;
  state?: string;
  codeChallenge?: string;
  codeChallengeMethod?: string;
  jwtDecoded?: any;
  timestamp: Date;
  isExpired: boolean;
}

interface Analytics {
  totalGenerated: number;
  totalBatches: number;
  tokenTypeDistribution: Record<string, number>;
  grantTypeDistribution: Record<string, number>;
  averageExpirationTime: number;
  scopeUsage: Record<string, number>;
  sessionTime: number;
  expiredTokens: number;
}

type ViewMode = 'grid' | 'list';
type SecurityLevel = 'basic' | 'enhanced' | 'enterprise';

const OAuthTokenGenerator: React.FC = () => {
  const [tokens, setTokens] = useState<GeneratedToken[]>([]);
  const [isGenerating, setIsGenerating] = useState<boolean>(false);
  const [isFullscreen, setIsFullscreen] = useState<boolean>(false);
  const [isDarkMode, setIsDarkMode] = useState<boolean>(false);
  const [showSettings, setShowSettings] = useState<boolean>(false);
  const [showAnalytics, setShowAnalytics] = useState<boolean>(false);
  const [viewMode, setViewMode] = useState<ViewMode>('grid');
  const [copiedId, setCopiedId] = useState<string>('');
  const [securityLevel, setSecurityLevel] = useState<SecurityLevel>('enhanced');
  const [showTokens, setShowTokens] = useState<boolean>(true);

  const [settings, setSettings] = useState<TokenSettings>({
    tokenType: 'access_token',
    grantType: 'authorization_code',
    expiresIn: 3600,
    batchSize: 6,
    includeScope: true,
    customScopes: ['read', 'write', 'admin'],
    includeRefreshToken: true,
    jwtHeader: { alg: 'HS256', typ: 'JWT' },
    jwtPayload: { iss: 'oauth-server', aud: 'client-app' },
    clientIdLength: 32,
    clientSecretLength: 64,
    useSecureRandom: true,
    includeState: true,
    includePKCE: false
  });

  const [analytics, setAnalytics] = useState<Analytics>({
    totalGenerated: 0,
    totalBatches: 0,
    tokenTypeDistribution: {},
    grantTypeDistribution: {},
    averageExpirationTime: 0,
    scopeUsage: {},
    sessionTime: 0,
    expiredTokens: 0
  });

  // SEO Meta Data
  const seoData = useMemo(() => {
    const baseTitle = "OAuth Token Generator";
    const dynamicTitle = analytics.totalGenerated > 0 
      ? `${baseTitle} - ${analytics.totalGenerated} OAuth Tokens Generated`
      : `${baseTitle} - Generate OAuth 2.0 & JWT Tokens`;
    
    const description = analytics.totalGenerated > 0
      ? `Professional OAuth token generator with ${analytics.totalGenerated} tokens created. Generate OAuth 2.0 access tokens, refresh tokens, JWT, and authorization codes. Average expiration: ${Math.round(analytics.averageExpirationTime/60)} minutes. Free developer tool.`
      : "Generate OAuth 2.0 tokens, JWT tokens, authorization codes, and client credentials. Support for all OAuth flows including PKCE, custom scopes, and token validation. Perfect for API testing and development.";

    const keywords = [
      "oauth token generator",
      "jwt token generator", 
      "oauth 2.0 token creator",
      "access token generator",
      "refresh token generator",
      "authorization code generator",
      "client credentials generator",
      "oauth testing tool",
      "jwt decoder",
      "api token generator",
      "oauth flow simulator",
      "bearer token generator",
      "oauth development tool",
      "jwt validation tool",
      "oauth security testing"
    ].join(", ");

    return { title: dynamicTitle, description, keywords };
  }, [analytics.totalGenerated, analytics.averageExpirationTime]);

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    "name": "OAuth Token Generator",
    "description": seoData.description,
    "url": "https://example.com/oauth-token-generator",
    "applicationCategory": "DeveloperApplication",
    "operatingSystem": "All",
    "permissions": "none required",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "USD"
    },
    "featureList": [
      "OAuth 2.0 token generation",
      "JWT token creation and validation",
      "Authorization code generation",
      "Client credentials management",
      "PKCE support",
      "Custom scope configuration",
      "Token expiration tracking",
      "Multiple OAuth flows",
      "Export functionality"
    ],
    "browserRequirements": "Requires JavaScript enabled",
    "publisher": {
      "@type": "Organization",
      "name": "Developer Tools"
    },
    "softwareVersion": "2.0",
    "datePublished": "2025-01-01",
    "dateModified": new Date().toISOString(),
    "inLanguage": "en-US",
    "isAccessibleForFree": true,
    "usageInfo": "Free to use for development and testing purposes"
  };

  // Common OAuth scopes
  const commonScopes = [
    'read', 'write', 'admin', 'user:read', 'user:write', 
    'repo', 'public_repo', 'openid', 'profile', 'email',
    'offline_access', 'api:read', 'api:write', 'full_access'
  ];

  const getRandomValue = useCallback((max: number): number => {
    if (securityLevel === 'enterprise' && window.crypto) {
      const array = new Uint32Array(1);
      window.crypto.getRandomValues(array);
      return array[0] % max;
    } else if (securityLevel === 'enhanced') {
      const now = Date.now();
      const random1 = Math.random();
      const random2 = Math.random();
      const seed = (now * random1 * random2) % 1;
      return Math.floor(seed * max);
    }
    return Math.floor(Math.random() * max);
  }, [securityLevel]);

  const generateRandomString = useCallback((length: number, charset?: string): string => {
    const defaultCharset = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    const chars = charset || defaultCharset;
    let result = '';
    for (let i = 0; i < length; i++) {
      result += chars.charAt(getRandomValue(chars.length));
    }
    return result;
  }, [getRandomValue]);

  const generateBase64UrlSafe = useCallback((length: number): string => {
    const charset = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_';
    return generateRandomString(length, charset);
  }, [generateRandomString]);

  const generateJWT = useCallback((): string => {
    const header = btoa(JSON.stringify(settings.jwtHeader)).replace(/=/g, '');
    const payload = {
      ...settings.jwtPayload,
      iat: Math.floor(Date.now() / 1000),
      exp: Math.floor(Date.now() / 1000) + settings.expiresIn,
      jti: generateRandomString(16)
    };
    const encodedPayload = btoa(JSON.stringify(payload)).replace(/=/g, '');
    const signature = generateBase64UrlSafe(32);
    return `${header}.${encodedPayload}.${signature}`;
  }, [settings, generateRandomString, generateBase64UrlSafe]);

  const generatePKCE = useCallback(() => {
    const codeVerifier = generateBase64UrlSafe(128);
    const codeChallenge = btoa(codeVerifier).replace(/=/g, '').replace(/\+/g, '-').replace(/\//g, '_');
    return {
      codeChallenge,
      codeChallengeMethod: 'S256'
    };
  }, [generateBase64UrlSafe]);

  const generateSingleToken = useCallback((): GeneratedToken => {
    const expiresAt = new Date(Date.now() + (settings.expiresIn * 1000));
    const scopes = settings.includeScope 
      ? settings.customScopes.length > 0 
        ? settings.customScopes 
        : ['read', 'write']
      : [];
    
    let tokenData: Partial<GeneratedToken> = {
      id: Math.random().toString(36).substr(2, 9),
      tokenType: settings.tokenType,
      grantType: settings.grantType,
      expiresIn: settings.expiresIn,
      expiresAt,
      scope: scopes.join(' '),
      timestamp: new Date(),
      isExpired: false
    };

    if (settings.includeState) {
      tokenData.state = generateRandomString(16);
    }

    if (settings.includePKCE) {
      const pkce = generatePKCE();
      tokenData.codeChallenge = pkce.codeChallenge;
      tokenData.codeChallengeMethod = pkce.codeChallengeMethod;
    }

    switch (settings.tokenType) {
      case 'access_token':
        tokenData.accessToken = `at_${generateRandomString(32)}`;
        tokenData.tokenValue = tokenData.accessToken;
        if (settings.includeRefreshToken) {
          tokenData.refreshToken = `rt_${generateRandomString(32)}`;
        }
        break;
      case 'refresh_token':
        tokenData.refreshToken = `rt_${generateRandomString(32)}`;
        tokenData.tokenValue = tokenData.refreshToken;
        break;
      case 'authorization_code':
        tokenData.authorizationCode = `ac_${generateRandomString(24)}`;
        tokenData.tokenValue = tokenData.authorizationCode;
        break;
      case 'jwt':
        tokenData.tokenValue = generateJWT();
        try {
          const parts = tokenData.tokenValue.split('.');
          const decodedPayload = JSON.parse(atob(parts[1]));
          tokenData.jwtDecoded = decodedPayload;
        } catch (e) {
          console.error('JWT decode error:', e);
        }
        break;
      case 'client_credentials':
        tokenData.clientId = generateRandomString(settings.clientIdLength);
        tokenData.clientSecret = generateRandomString(settings.clientSecretLength);
        tokenData.tokenValue = `${tokenData.clientId}:${tokenData.clientSecret}`;
        break;
    }

    return tokenData as GeneratedToken;
  }, [settings, generateRandomString, generateJWT, generatePKCE]);

  const generateTokenBatch = useCallback(async () => {
    setIsGenerating(true);
    const newTokens: GeneratedToken[] = [];
    
    const chunkSize = 2;
    for (let i = 0; i < settings.batchSize; i += chunkSize) {
      const chunk = Math.min(chunkSize, settings.batchSize - i);
      for (let j = 0; j < chunk; j++) {
        newTokens.push(generateSingleToken());
      }
      
      await new Promise(resolve => setTimeout(resolve, 50));
    }

    setTokens(prev => [...newTokens, ...prev].slice(0, 500));
    
    // Update analytics
    setAnalytics(prev => {
      const total = prev.totalGenerated + newTokens.length;
      const avgExpiration = (prev.averageExpirationTime * prev.totalGenerated + 
                           newTokens.reduce((sum, t) => sum + t.expiresIn, 0)) / total;
      
      const tokenTypeDist = { ...prev.tokenTypeDistribution };
      const grantTypeDist = { ...prev.grantTypeDistribution };
      const scopeUsage = { ...prev.scopeUsage };
      
      newTokens.forEach(token => {
        tokenTypeDist[token.tokenType] = (tokenTypeDist[token.tokenType] || 0) + 1;
        grantTypeDist[token.grantType] = (grantTypeDist[token.grantType] || 0) + 1;
        
        if (token.scope) {
          token.scope.split(' ').forEach(scope => {
            scopeUsage[scope] = (scopeUsage[scope] || 0) + 1;
          });
        }
      });

      return {
        totalGenerated: total,
        totalBatches: prev.totalBatches + 1,
        tokenTypeDistribution: tokenTypeDist,
        grantTypeDistribution: grantTypeDist,
        averageExpirationTime: Math.round(avgExpiration),
        scopeUsage,
        sessionTime: prev.sessionTime,
        expiredTokens: prev.expiredTokens
      };
    });

    setIsGenerating(false);
  }, [settings.batchSize, generateSingleToken]);

  const copyToken = async (tokenValue: string, id: string) => {
    try {
      await navigator.clipboard.writeText(tokenValue);
      setCopiedId(id);
      setTimeout(() => setCopiedId(''), 2000);
    } catch (err) {
      console.error('Failed to copy token');
    }
  };

  const copyAllTokens = async () => {
    const allTokens = tokens.map(t => t.tokenValue).join('\n');
    try {
      await navigator.clipboard.writeText(allTokens);
      setCopiedId('all');
      setTimeout(() => setCopiedId(''), 2000);
    } catch (err) {
      console.error('Failed to copy tokens');
    }
  };

  const exportTokens = () => {
    const data = {
      tokens: tokens.map(t => ({
        tokenType: t.tokenType,
        grantType: t.grantType,
        tokenValue: t.tokenValue,
        expiresIn: t.expiresIn,
        expiresAt: t.expiresAt,
        scope: t.scope,
        timestamp: t.timestamp,
        jwtDecoded: t.jwtDecoded
      })),
      settings,
      analytics,
      exportDate: new Date()
    };
    
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `oauth_tokens_${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const clearAllTokens = () => {
    setTokens([]);
  };

  const getTokenTypeColor = (tokenType: string): string => {
    switch (tokenType) {
      case 'access_token': return 'text-green-500';
      case 'refresh_token': return 'text-blue-500';
      case 'authorization_code': return 'text-purple-500';
      case 'jwt': return 'text-orange-500';
      case 'client_credentials': return 'text-red-500';
      default: return 'text-gray-500';
    }
  };

  const getSecurityLevelColor = (level: SecurityLevel): string => {
    switch (level) {
      case 'basic': return 'bg-yellow-500';
      case 'enhanced': return 'bg-blue-500';
      case 'enterprise': return 'bg-green-500';
    }
  };

  const formatTimeRemaining = (expiresAt: Date): string => {
    const now = new Date();
    const diff = expiresAt.getTime() - now.getTime();
    if (diff <= 0) return 'Expired';
    
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);
    
    if (days > 0) return `${days}d ${hours % 24}h`;
    if (hours > 0) return `${hours}h ${minutes % 60}m`;
    return `${minutes}m`;
  };

  useEffect(() => {
    generateTokenBatch();
  }, []);

  // Session time tracking
  useEffect(() => {
    const interval = setInterval(() => {
      setAnalytics(prev => ({ ...prev, sessionTime: prev.sessionTime + 1 }));
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  // Check for expired tokens
  useEffect(() => {
    const interval = setInterval(() => {
      const now = new Date();
      setTokens(prev => prev.map(token => ({
        ...token,
        isExpired: now > token.expiresAt
      })));
      
      setAnalytics(prev => ({
        ...prev,
        expiredTokens: tokens.filter(t => new Date() > t.expiresAt).length
      }));
    }, 60000); // Check every minute
    
    return () => clearInterval(interval);
  }, [tokens]);

  const containerClasses = `
    ${isFullscreen ? 'fixed inset-0 z-50' : 'min-h-screen'}
    ${isDarkMode ? 'dark bg-gray-900' : 'bg-gradient-to-br from-blue-50 to-indigo-100'}
    transition-all duration-300
  `;

  return (
    <div className={containerClasses}>
      <Helmet>
        {/* Primary Meta Tags */}
        <title>{seoData.title}</title>
        <meta name="title" content={seoData.title} />
        <meta name="description" content={seoData.description} />
        <meta name="keywords" content={seoData.keywords} />
        <meta name="author" content="Developer Tools" />
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
        <meta name="language" content="English" />
        <meta name="revisit-after" content="7 days" />
        <meta name="rating" content="General" />
        <meta name="distribution" content="global" />
        
        {/* Canonical URL */}
        <link rel="canonical" href="https://example.com/oauth-token-generator" />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://example.com/oauth-token-generator" />
        <meta property="og:title" content={seoData.title} />
        <meta property="og:description" content={seoData.description} />
        <meta property="og:image" content="https://example.com/oauth-generator-preview.jpg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content="OAuth Token Generator Tool Interface" />
        <meta property="og:site_name" content="Developer Tools" />
        <meta property="og:locale" content="en_US" />
        
        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content="https://example.com/oauth-token-generator" />
        <meta property="twitter:title" content={seoData.title} />
        <meta property="twitter:description" content={seoData.description} />
        <meta property="twitter:image" content="https://example.com/oauth-generator-preview.jpg" />
        <meta property="twitter:image:alt" content="Advanced OAuth Token Generator with Analytics" />
        <meta name="twitter:creator" content="@devtools" />
        <meta name="twitter:site" content="@devtools" />
        
        {/* Additional SEO Meta Tags */}
        <meta name="theme-color" content={isDarkMode ? "#1f2937" : "#3b82f6"} />
        <meta name="msapplication-TileColor" content="#3b82f6" />
        <meta name="application-name" content="OAuth Token Generator" />
        <meta name="apple-mobile-web-app-title" content="OAuth Generator" />
        <meta name="apple-mobile-web-app-capable" content="yes" />
        <meta name="apple-mobile-web-app-status-bar-style" content="default" />
        <meta name="format-detection" content="telephone=no" />
        
        {/* Performance and Security */}
        <meta httpEquiv="X-UA-Compatible" content="IE=edge" />
        <meta name="referrer" content="origin-when-cross-origin" />
        
        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
        
        {/* Additional Schema for Tool */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "SoftwareApplication",
            "name": "OAuth Token Generator",
            "description": "Professional OAuth 2.0 and JWT token generator for API development and testing",
            "applicationCategory": "DeveloperApplication",
            "operatingSystem": "Web Browser",
            "downloadUrl": "https://example.com/oauth-token-generator",
            "softwareVersion": "2.0",
            "datePublished": "2025-01-01",
            "publisher": {
              "@type": "Organization",
              "name": "Developer Tools"
            },
            "offers": {
              "@type": "Offer",
              "price": "0",
              "priceCurrency": "USD",
              "availability": "https://schema.org/InStock"
            },
            "aggregateRating": {
              "@type": "AggregateRating",
              "ratingValue": "4.9",
              "reviewCount": "856",
              "bestRating": "5",
              "worstRating": "1"
            }
          })}
        </script>
        
        {/* FAQ Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": "What OAuth token types can be generated?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Generate access tokens, refresh tokens, authorization codes, JWT tokens, and client credentials. Supports all major OAuth 2.0 flows including authorization code, client credentials, and resource owner password."
                }
              },
              {
                "@type": "Question", 
                "name": "Are the generated tokens secure for production use?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "These tokens are for development and testing purposes only. For production, use a proper OAuth server with secure key management and validated implementations."
                }
              },
              {
                "@type": "Question",
                "name": "What is PKCE support?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "PKCE (Proof Key for Code Exchange) adds security for OAuth flows by using code challenge and verifier. Essential for mobile and single-page applications."
                }
              },
              {
                "@type": "Question",
                "name": "Can I customize token scopes?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes, add custom scopes or use common ones like 'read', 'write', 'admin'. Scopes define the permissions and access levels for OAuth tokens."
                }
              }
            ]
          })}
        </script>
        
        {/* Dynamic Analytics Schema */}
        {analytics.totalGenerated > 0 && (
          <script type="application/ld+json">
            {JSON.stringify({
              "@context": "https://schema.org",
              "@type": "Action",
              "actionStatus": "CompletedActionStatus",
              "object": {
                "@type": "CreativeWork",
                "name": `${analytics.totalGenerated} OAuth Tokens Generated`,
                "description": `Session statistics: ${analytics.totalBatches} batches, ${analytics.expiredTokens} expired tokens`
              },
              "startTime": new Date(Date.now() - analytics.sessionTime * 1000).toISOString(),
              "endTime": new Date().toISOString()
            })}
          </script>
        )}
      </Helmet>
      
      <div className={`${isDarkMode ? 'dark' : ''}`}>
        <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-gray-900 dark:to-gray-800 p-4 transition-colors duration-300">
          {/* Header */}
          <div className="max-w-7xl mx-auto mb-6">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center bg-white dark:bg-gray-800 rounded-lg shadow-lg p-4 gap-4">
              <div className="flex items-center space-x-3">
                <Key className="w-8 h-8 text-blue-600 dark:text-blue-400" aria-hidden="true" />
                <div>
                  <h1 className="text-2xl font-bold text-gray-800 dark:text-white">
                    OAuth Token Generator
                  </h1>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Generate OAuth 2.0 tokens, JWT, and authorization codes for API testing
                  </p>
                </div>
              </div>
              
              <div className="flex items-center space-x-2 flex-wrap">
                {/* Security Level */}
                <div className="flex items-center space-x-2">
                  <span className="text-xs text-gray-600 dark:text-gray-400">Security:</span>
                  <select
                    value={securityLevel}
                    onChange={(e) => setSecurityLevel(e.target.value as SecurityLevel)}
                    className="text-xs px-2 py-1 rounded bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300"
                    aria-label="Select security level for token generation"
                  >
                    <option value="basic">Basic</option>
                    <option value="enhanced">Enhanced</option>
                    <option value="enterprise">Enterprise</option>
                  </select>
                  <div className={`w-2 h-2 rounded-full ${getSecurityLevelColor(securityLevel)}`} />
                </div>

                {/* View Mode */}
                <div className="flex border border-gray-300 dark:border-gray-600 rounded-lg overflow-hidden">
                  <button
                    onClick={() => setViewMode('grid')}
                    className={`p-2 ${viewMode === 'grid' ? 'bg-blue-100 dark:bg-blue-900' : 'bg-white dark:bg-gray-700'} transition-colors`}
                    title="Grid View"
                  >
                    <Grid className="w-4 h-4 text-gray-600 dark:text-gray-300" />
                  </button>
                  <button
                    onClick={() => setViewMode('list')}
                    className={`p-2 ${viewMode === 'list' ? 'bg-blue-100 dark:bg-blue-900' : 'bg-white dark:bg-gray-700'} transition-colors`}
                    title="List View"
                  >
                    <List className="w-4 h-4 text-gray-600 dark:text-gray-300" />
                  </button>
                </div>

                {/* Action Buttons */}
                <button
                  onClick={() => setShowAnalytics(!showAnalytics)}
                  className="p-2 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                  title="Analytics"
                >
                  <BarChart3 className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                </button>
                
                <button
                  onClick={() => setShowSettings(!showSettings)}
                  className="p-2 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                  title="Settings"
                >
                  <Settings className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                </button>
                
                <button
                  onClick={() => setIsDarkMode(!isDarkMode)}
                  className="p-2 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                  title="Toggle Theme"
                >
                  {isDarkMode ? (
                    <Sun className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                  ) : (
                    <Moon className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                  )}
                </button>
                
                <button
                  onClick={() => setIsFullscreen(!isFullscreen)}
                  className="p-2 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                  title={isFullscreen ? "Exit Fullscreen" : "Enter Fullscreen"}
                >
                  {isFullscreen ? (
                    <Minimize className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                  ) : (
                    <Maximize className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                  )}
                </button>
              </div>
            </div>
          </div>

          <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-4 gap-6">
            {/* Main Content */}
            <div className={`${showSettings || showAnalytics ? 'lg:col-span-3' : 'lg:col-span-4'}`}>
              {/* Control Panel */}
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-4 mb-6">
                <div className="flex flex-wrap items-center justify-between gap-4">
                  <div className="flex items-center space-x-4">
                    <button
                      onClick={generateTokenBatch}
                      disabled={isGenerating}
                      className="bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white font-medium py-2 px-4 rounded-lg transition-colors duration-200 flex items-center space-x-2"
                      aria-label={`Generate ${settings.batchSize} OAuth tokens`}
                    >
                      {isGenerating ? (
                        <RefreshCw className="w-5 h-5 animate-spin" />
                      ) : (
                        <Zap className="w-5 h-5" />
                      )}
                      <span>{isGenerating ? 'Generating...' : `Generate ${settings.batchSize}`}</span>
                    </button>

                    <div className="flex items-center space-x-2">
                      <span className="text-sm text-gray-600 dark:text-gray-400">Type:</span>
                      <select
                        value={settings.tokenType}
                        onChange={(e) => setSettings(prev => ({ ...prev, tokenType: e.target.value as any }))}
                        className="text-sm px-2 py-1 rounded bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300"
                      >
                        <option value="access_token">Access Token</option>
                        <option value="refresh_token">Refresh Token</option>
                        <option value="authorization_code">Auth Code</option>
                        <option value="jwt">JWT Token</option>
                        <option value="client_credentials">Client Credentials</option>
                      </select>
                    </div>

                    <div className="flex items-center space-x-2">
                      <span className="text-sm text-gray-600 dark:text-gray-400">Batch:</span>
                      <div className="flex items-center space-x-1">
                        <button
                          onClick={() => setSettings(prev => ({ ...prev, batchSize: Math.max(1, prev.batchSize - 1) }))}
                          className="p-1 rounded bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600"
                        >
                          <Minus className="w-3 h-3" />
                        </button>
                        <span className="w-8 text-center text-sm font-medium text-gray-800 dark:text-white">
                          {settings.batchSize}
                        </span>
                        <button
                          onClick={() => setSettings(prev => ({ ...prev, batchSize: Math.min(20, prev.batchSize + 1) }))}
                          className="p-1 rounded bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600"
                        >
                          <Plus className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => setShowTokens(!showTokens)}
                      className="p-2 rounded bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                      title={showTokens ? "Hide Tokens" : "Show Tokens"}
                    >
                      {showTokens ? (
                        <EyeOff className="w-4 h-4 text-gray-600 dark:text-gray-300" />
                      ) : (
                        <Eye className="w-4 h-4 text-gray-600 dark:text-gray-300" />
                      )}
                    </button>

                    <button
                      onClick={copyAllTokens}
                      className="p-2 rounded bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                      title="Copy All Tokens"
                    >
                      {copiedId === 'all' ? (
                        <Check className="w-4 h-4 text-green-500" />
                      ) : (
                        <Copy className="w-4 h-4 text-gray-600 dark:text-gray-300" />
                      )}
                    </button>

                    <button
                      onClick={exportTokens}
                      className="p-2 rounded bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                      title="Export Tokens"
                    >
                      <Download className="w-4 h-4 text-gray-600 dark:text-gray-300" />
                    </button>

                    <button
                      onClick={clearAllTokens}
                      className="p-2 rounded bg-red-100 dark:bg-red-900 hover:bg-red-200 dark:hover:bg-red-800 transition-colors"
                      title="Clear All"
                    >
                      <Trash2 className="w-4 h-4 text-red-600 dark:text-red-400" />
                    </button>
                  </div>
                </div>

                <div className="mt-4 flex items-center justify-between text-sm text-gray-600 dark:text-gray-400">
                  <span>{tokens.length} tokens generated</span>
                  <span>Expires in: {Math.floor(settings.expiresIn / 60)} minutes</span>
                </div>
              </div>

              {/* Token Grid/List */}
              {tokens.length > 0 ? (
                <div className={`
                  ${viewMode === 'grid' 
                    ? 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4' 
                    : 'space-y-3'
                  }
                `}>
                  {tokens.map((token) => (
                    <div
                      key={token.id}
                      className={`
                        bg-white dark:bg-gray-800 rounded-lg shadow-md hover:shadow-lg transition-shadow border-l-4
                        ${token.isExpired ? 'border-red-500' : 'border-blue-500'}
                        ${viewMode === 'list' ? 'p-4' : 'p-4'}
                      `}
                    >
                      {viewMode === 'grid' ? (
                        <div className="space-y-3">
                          <div className="flex items-center justify-between">
                            <span className={`text-xs font-medium px-2 py-1 rounded ${getTokenTypeColor(token.tokenType)} bg-gray-100 dark:bg-gray-700`}>
                              {token.tokenType.replace('_', ' ')}
                            </span>
                            <div className="flex items-center space-x-1">
                              <Clock className="w-3 h-3 text-gray-400" />
                              <span className="text-xs text-gray-500 dark:text-gray-400">
                                {formatTimeRemaining(token.expiresAt)}
                              </span>
                            </div>
                          </div>
                          
                          <div className="relative">
                            <input
                              type={showTokens ? "text" : "password"}
                              value={token.tokenValue}
                              readOnly
                              className="w-full p-2 text-xs font-mono bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded focus:outline-none"
                            />
                          </div>
                          
                          {token.scope && (
                            <div className="flex flex-wrap gap-1">
                              {token.scope.split(' ').map((scope, idx) => (
                                <span key={idx} className="text-xs px-2 py-1 bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 rounded">
                                  {scope}
                                </span>
                              ))}
                            </div>
                          )}
                          
                          <div className="flex items-center justify-between">
                            <span className="text-xs text-gray-400 dark:text-gray-500">
                              {token.grantType.replace('_', ' ')}
                            </span>
                            <button
                              onClick={() => copyToken(token.tokenValue, token.id)}
                              className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                            >
                              {copiedId === token.id ? (
                                <Check className="w-4 h-4 text-green-500" />
                              ) : (
                                <Copy className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                              )}
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center justify-between">
                          <div className="flex-1 min-w-0 space-y-2">
                            <div className="flex items-center space-x-2">
                              <span className={`text-xs font-medium px-2 py-1 rounded ${getTokenTypeColor(token.tokenType)} bg-gray-100 dark:bg-gray-700`}>
                                {token.tokenType.replace('_', ' ')}
                              </span>
                              <span className="text-xs text-gray-500 dark:text-gray-400">
                                {formatTimeRemaining(token.expiresAt)}
                              </span>
                            </div>
                            <input
                              type={showTokens ? "text" : "password"}
                              value={token.tokenValue}
                              readOnly
                              className="w-full p-2 text-xs font-mono bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded focus:outline-none"
                            />
                          </div>
                          <div className="ml-4 flex items-center space-x-2">
                            <button
                              onClick={() => copyToken(token.tokenValue, token.id)}
                              className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                            >
                              {copiedId === token.id ? (
                                <Check className="w-4 h-4 text-green-500" />
                              ) : (
                                <Copy className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                              )}
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-12 text-center">
                  <Shield className="w-16 h-16 text-gray-400 dark:text-gray-500 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-800 dark:text-white mb-2">
                    No OAuth tokens generated yet
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400 mb-6">
                    Generate OAuth 2.0 tokens, JWT, authorization codes, and client credentials
                  </p>
                  <button
                    onClick={generateTokenBatch}
                    className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-6 rounded-lg transition-colors duration-200"
                  >
                    Generate Tokens
                  </button>
                </div>
              )}
            </div>

            {/* Settings Panel */}
            {showSettings && (
              <div className="lg:col-span-1">
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 sticky top-4 max-h-screen overflow-y-auto">
                  <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4">
                    OAuth Settings
                  </h3>
                  
                  <div className="space-y-6">
                    {/* Token Configuration */}
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Token Configuration</h4>
                      <div className="space-y-3">
                        <div>
                          <label className="block text-sm text-gray-600 dark:text-gray-400 mb-1">Token Type</label>
                          <select
                            value={settings.tokenType}
                            onChange={(e) => setSettings(prev => ({ ...prev, tokenType: e.target.value as any }))}
                            className="w-full p-2 text-sm bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-blue-500"
                          >
                            <option value="access_token">Access Token</option>
                            <option value="refresh_token">Refresh Token</option>
                            <option value="authorization_code">Authorization Code</option>
                            <option value="jwt">JWT Token</option>
                            <option value="client_credentials">Client Credentials</option>
                          </select>
                        </div>
                        
                        <div>
                          <label className="block text-sm text-gray-600 dark:text-gray-400 mb-1">Grant Type</label>
                          <select
                            value={settings.grantType}
                            onChange={(e) => setSettings(prev => ({ ...prev, grantType: e.target.value as any }))}
                            className="w-full p-2 text-sm bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-blue-500"
                          >
                            <option value="authorization_code">Authorization Code</option>
                            <option value="client_credentials">Client Credentials</option>
                            <option value="resource_owner">Resource Owner Password</option>
                            <option value="refresh_token">Refresh Token</option>
                          </select>
                        </div>
                        
                        <div>
                          <label className="block text-sm text-gray-600 dark:text-gray-400 mb-1">
                            Expires In: {Math.floor(settings.expiresIn / 60)} minutes
                          </label>
                          <input
                            type="range"
                            min="300"
                            max="86400"
                            step="300"
                            value={settings.expiresIn}
                            onChange={(e) => setSettings(prev => ({ ...prev, expiresIn: parseInt(e.target.value) }))}
                            className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer dark:bg-gray-700"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Scopes */}
                    <div>
                      <label className="flex items-center space-x-3 cursor-pointer mb-2">
                        <input
                          type="checkbox"
                          checked={settings.includeScope}
                          onChange={(e) => setSettings(prev => ({ ...prev, includeScope: e.target.checked }))}
                          className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                        />
                        <span className="text-sm text-gray-700 dark:text-gray-300">Include Scopes</span>
                      </label>
                      
                      {settings.includeScope && (
                        <div className="space-y-2">
                          <div className="flex flex-wrap gap-1">
                            {commonScopes.map(scope => (
                              <button
                                key={scope}
                                onClick={() => {
                                  const newScopes = settings.customScopes.includes(scope)
                                    ? settings.customScopes.filter(s => s !== scope)
                                    : [...settings.customScopes, scope];
                                  setSettings(prev => ({ ...prev, customScopes: newScopes }));
                                }}
                                className={`text-xs px-2 py-1 rounded transition-colors ${
                                  settings.customScopes.includes(scope)
                                    ? 'bg-blue-500 text-white'
                                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                                }`}
                              >
                                {scope}
                              </button>
                            ))}
                          </div>
                          <input
                            type="text"
                            placeholder="Add custom scope..."
                            className="w-full p-2 text-sm bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-blue-500"
                            onKeyPress={(e) => {
                              if (e.key === 'Enter') {
                                const value = e.currentTarget.value.trim();
                                if (value && !settings.customScopes.includes(value)) {
                                  setSettings(prev => ({ ...prev, customScopes: [...prev.customScopes, value] }));
                                  e.currentTarget.value = '';
                                }
                              }
                            }}
                          />
                        </div>
                      )}
                    </div>

                    {/* Advanced Options */}
                    <div className="space-y-3">
                      <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">Advanced Options</h4>
                      {[
                        { key: 'includeRefreshToken', label: 'Include Refresh Token', desc: 'Generate refresh token with access token' },
                        { key: 'includeState', label: 'Include State', desc: 'CSRF protection parameter' },
                        { key: 'includePKCE', label: 'Include PKCE', desc: 'Proof Key for Code Exchange' },
                        { key: 'useSecureRandom', label: 'Secure Random', desc: 'Use cryptographically secure randomness' }
                      ].map(({ key, label, desc }) => (
                        <div key={key}>
                          <label className="flex items-center space-x-3 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={settings[key as keyof TokenSettings] as boolean}
                              onChange={(e) => setSettings(prev => ({ ...prev, [key]: e.target.checked }))}
                              className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                            />
                            <div>
                              <span className="text-sm text-gray-700 dark:text-gray-300">{label}</span>
                              <p className="text-xs text-gray-500 dark:text-gray-400">{desc}</p>
                            </div>
                          </label>
                        </div>
                      ))}
                    </div>

                    {/* JWT Configuration */}
                    {settings.tokenType === 'jwt' && (
                      <div>
                        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">JWT Configuration</h4>
                        <div className="space-y-2">
                          <div>
                            <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">JWT Header</label>
                            <textarea
                              value={JSON.stringify(settings.jwtHeader, null, 2)}
                              onChange={(e) => {
                                try {
                                  const header = JSON.parse(e.target.value);
                                  setSettings(prev => ({ ...prev, jwtHeader: header }));
                                } catch (err) {
                                  // Invalid JSON, ignore
                                }
                              }}
                              className="w-full p-2 text-xs font-mono bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-blue-500"
                              rows={3}
                            />
                          </div>
                          <div>
                            <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">JWT Payload</label>
                            <textarea
                              value={JSON.stringify(settings.jwtPayload, null, 2)}
                              onChange={(e) => {
                                try {
                                  const payload = JSON.parse(e.target.value);
                                  setSettings(prev => ({ ...prev, jwtPayload: payload }));
                                } catch (err) {
                                  // Invalid JSON, ignore
                                }
                              }}
                              className="w-full p-2 text-xs font-mono bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-blue-500"
                              rows={4}
                            />
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Analytics Panel */}
            {showAnalytics && (
              <div className="lg:col-span-1">
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 sticky top-4">
                  <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4">
                    Analytics
                  </h3>
                  
                  <div className="space-y-4">
                    {/* Quick Stats */}
                    <div className="grid grid-cols-2 gap-3">
                      <div className="text-center p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                        <div className="text-xl font-bold text-blue-600 dark:text-blue-400">
                          {analytics.totalGenerated}
                        </div>
                        <div className="text-xs text-gray-600 dark:text-gray-400">
                          Total Generated
                        </div>
                      </div>
                      <div className="text-center p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
                        <div className="text-xl font-bold text-green-600 dark:text-green-400">
                          {analytics.totalBatches}
                        </div>
                        <div className="text-xs text-gray-600 dark:text-gray-400">
                          Batches
                        </div>
                      </div>
                      <div className="text-center p-3 bg-orange-50 dark:bg-orange-900/20 rounded-lg">
                        <div className="text-xl font-bold text-orange-600 dark:text-orange-400">
                          {Math.floor(analytics.averageExpirationTime / 60)}m
                        </div>
                        <div className="text-xs text-gray-600 dark:text-gray-400">
                          Avg Expiration
                        </div>
                      </div>
                      <div className="text-center p-3 bg-red-50 dark:bg-red-900/20 rounded-lg">
                        <div className="text-xl font-bold text-red-600 dark:text-red-400">
                          {analytics.expiredTokens}
                        </div>
                        <div className="text-xs text-gray-600 dark:text-gray-400">
                          Expired
                        </div>
                      </div>
                    </div>

                    {/* Token Type Distribution */}
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Token Types
                      </h4>
                      <div className="space-y-2">
                        {Object.entries(analytics.tokenTypeDistribution).map(([type, count]) => (
                          <div key={type} className="flex justify-between items-center text-sm">
                            <span className="text-gray-600 dark:text-gray-400">
                              {type.replace('_', ' ')}
                            </span>
                            <span className="font-medium text-gray-800 dark:text-white">
                              {count}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Popular Scopes */}
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Popular Scopes
                      </h4>
                      <div className="space-y-1 max-h-32 overflow-y-auto">
                        {Object.entries(analytics.scopeUsage)
                          .sort(([,a], [,b]) => b - a)
                          .slice(0, 10)
                          .map(([scope, count]) => (
                          <div key={scope} className="flex justify-between items-center text-xs text-gray-600 dark:text-gray-400">
                            <span>{scope}</span>
                            <span>{count} times</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Security Info */}
                    <div className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                      <div className="flex items-center space-x-2 mb-2">
                        <AlertCircle className="w-4 h-4 text-blue-500" />
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                          Security Level
                        </span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <div className={`w-2 h-2 rounded-full ${getSecurityLevelColor(securityLevel)}`} />
                        <span className="text-sm text-gray-600 dark:text-gray-400 capitalize">
                          {securityLevel}
                        </span>
                      </div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        {securityLevel === 'enterprise' 
                          ? 'Uses crypto.getRandomValues()' 
                          : securityLevel === 'enhanced'
                          ? 'Multiple entropy sources'
                          : 'Standard Math.random()'}
                      </p>
                    </div>

                    {/* Session Time */}
                    <div className="text-center text-sm text-gray-600 dark:text-gray-400">
                      Session: {Math.floor(analytics.sessionTime / 60)}m {analytics.sessionTime % 60}s
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OAuthTokenGenerator;